<template>
  <b-container fluid>
    <b-row>
      <b-col class="d-flex">
        <backlink class="mt-20" />
        <div class="flex-grow-1">
          <h1>
            {{ values.request_kind || config.singleLabel }}
            <div v-if="!isBusy" class="subtitle">#{{ values.internal_code }}</div>
            <b-badge v-if="!isBusy" :variant="isHandled ? 'success' : 'primary'">{{
              values.status
            }}</b-badge>
          </h1>
        </div>
        <div v-if="values.id" class="text-right">
          <b-button
            v-if="!isHandled && canEdit"
            variant="outline-primary ml-10"
            size="lg"
            @click="approuve()"
          >
            Marquer comme traité
            <i class="far fa-check fa-lg ml-10"></i>
          </b-button>
          <b-button
            variant="outline-primary ml-10"
            size="lg"
            @click="$root.exportPdf(exportPdfData)"
          >
            Exporter en PDF
            <i class="far fa-file-alt fa-lg ml-10"></i>
          </b-button>
        </div>
      </b-col>
    </b-row>

    <div v-if="isBusy" class="loader"></div>
    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>

    <b-row v-else>
      <b-col>
        <div class="ibox">
          <div class="ibox-content divided">
            <b-row>
              <!-- Ce qui a été demandé : la demande elle-même et, en évidence, son
                   message — c'est lui qu'on vient lire en ouvrant la fiche. -->
              <b-col cols="5">
                <h3>La demande</h3>
                <dl>
                  <dt>{{ labels.created_at }} :</dt>
                  <dd>{{ values.created_at || "-" }}</dd>
                  <template v-if="values.noticed_at">
                    <dt>{{ labels.noticed_at }} :</dt>
                    <dd>{{ values.noticed_at }}</dd>
                  </template>
                  <dt>{{ labels.request_kind }} :</dt>
                  <dd>{{ values.request_kind || "-" }}</dd>
                  <dt>{{ labels.context_label }} :</dt>
                  <dd>{{ values.context_label || "-" }}</dd>
                  <dt>{{ labels.complaint_type_slug }} :</dt>
                  <dd>{{ values.complaint_type_slug || "-" }}</dd>
                </dl>
                <h3 class="mt-20">{{ labels.message }}</h3>
                <p class="nl2br mb-0">{{ values.message || "-" }}</p>
              </b-col>

              <!-- Sur quoi elle porte : seuls les rattachements réellement renseignés.
                   Une demande en porte deux ou trois selon son contexte ; aligner des
                   tirets sur ceux qui manquent n'apprend rien. -->
              <b-col cols="4">
                <h3>Objet de la demande</h3>
                <dl v-if="hasTargets">
                  <template v-if="values.reference">
                    <dt>{{ labels.reference }} :</dt>
                    <dd>
                      <router-link
                        :to="{ name: 'ReferenceDetail', params: { id: rawValues.reference.id } }"
                        target="_blank"
                        >{{ values.reference }}</router-link
                      >
                    </dd>
                  </template>
                  <template v-if="values.product">
                    <dt>{{ labels.product }} :</dt>
                    <dd>
                      <router-link
                        :to="{ name: 'ProductDetail', params: { id: rawValues.product.id } }"
                        target="_blank"
                        >{{ values.product }}</router-link
                      >
                    </dd>
                  </template>
                  <template v-if="values.renting">
                    <dt>{{ labels.renting }} :</dt>
                    <dd>
                      <router-link
                        :to="{ name: 'RentingDetail', params: { id: rawValues.renting.id } }"
                        target="_blank"
                        >{{ values.renting }}</router-link
                      >
                    </dd>
                  </template>
                  <template v-if="values.business">
                    <dt>{{ labels.business }} :</dt>
                    <dd>{{ values.business }}</dd>
                  </template>
                  <template v-if="values.transfer">
                    <dt>{{ labels.transfer }} :</dt>
                    <dd>
                      <router-link
                        :to="{ name: 'TransferDetail', params: { id: rawValues.transfer.id } }"
                        target="_blank"
                        >{{ values.transfer }}</router-link
                      >
                    </dd>
                  </template>
                  <template v-if="values.business_return">
                    <dt>{{ labels.business_return }} :</dt>
                    <dd>
                      <router-link
                        :to="{
                          name: 'BusinessReturnDetail',
                          params: { id: rawValues.business_return.id },
                        }"
                        target="_blank"
                        >{{ values.business_return }}</router-link
                      >
                    </dd>
                  </template>
                </dl>
                <p v-else class="mb-0">Aucun élément rattaché à cette demande.</p>
              </b-col>

              <!-- Qui a demandé, et où en est le traitement : les deux informations qu'on
                   cherche pour reprendre contact ou pour savoir s'il reste à agir. -->
              <b-col cols="3">
                <h3>Demandeur</h3>
                <ul v-if="rawValues.user">
                  <li>
                    <i class="far fa-user"></i>
                    {{ rawValues.user.firstname }} {{ rawValues.user.lastname }}
                  </li>
                  <li v-if="rawValues.user.email">
                    <i class="far fa-envelope"></i>
                    <a :href="'mailto:' + rawValues.user.email">{{ rawValues.user.email }}</a>
                  </li>
                  <li>
                    <i class="far fa-phone"></i>
                    <a v-if="values.phone_number" :href="'tel:' + values.phone_number">{{
                      values.phone_number
                    }}</a>
                    <template v-else>-</template>
                  </li>
                </ul>
                <ul v-else>
                  <li>
                    <i class="far fa-user"></i>
                    Demande importée (MCS)
                  </li>
                </ul>

                <h3>Traitement</h3>
                <dl>
                  <dt>{{ labels.status }} :</dt>
                  <dd>{{ values.status || "-" }}</dd>
                  <dt>{{ labels.handled_by }} :</dt>
                  <dd>{{ values.handled_by || "-" }}</dd>
                </dl>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="!isBusy">
      <!-- Remarques : suivi interne du traitement, sur le modèle du pavé des feuilles
           d'expédition. Saisi ici plutôt que dans un formulaire de modification : la fiche
           n'en a pas (hideActionEditButton), et il n'y a rien d'autre à y modifier. -->
      <b-col cols="8">
        <div class="ibox">
          <div class="ibox-title">
            <i class="far fa-comment-alt"></i>
            {{ labels.comment }}
          </div>
          <div class="ibox-content">
            <template v-if="canEdit">
              <b-form-textarea
                v-model="comment"
                rows="5"
                placeholder="Noter une remarque sur le traitement de cette demande"
              ></b-form-textarea>
              <div class="text-right mt-20">
                <b-button variant="primary" :disabled="isSaving" @click="saveComment">
                  Enregistrer les remarques
                  <i class="far fa-save ml-10"></i>
                </b-button>
              </div>
            </template>
            <p v-else class="nl2br mb-0">{{ values.comment || "-" }}</p>
          </div>
        </div>
      </b-col>

      <b-col cols="4">
        <div class="ibox">
          <div class="ibox-title">
            <i class="far fa-folder"></i>
            Fichiers joints
          </div>
          <div class="ibox-content">
            <ul v-if="rawValues.stored_files && rawValues.stored_files.length" class="mb-0">
              <li v-for="storedFile in rawValues.stored_files" :key="storedFile.id">
                <i class="far fa-paperclip"></i>
                <a :href="filesUrl + storedFile.file" target="_blank">{{ storedFile.file }}</a>
              </li>
            </ul>
            <p v-else class="mb-0">Aucun fichier joint.</p>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import config from "./config";

export default {
  name: "ComplaintDetail",
  data() {
    return {
      config: config,
      labels: {},
      comment: "",
      isBusy: false,
      isSaving: false,
      filesUrl: process.env.BACK_FILES_URL,
    };
  },
  computed: {
    rawValues: function () {
      return (
        this.$store.getters[config.storeNamespace + "/getItem"](this.$route.params.id) || {
          status: {},
        }
      );
    },
    /** Champs passés par leurs formatteurs : libellés de statut, dates, natures... */
    values: function () {
      return this.$root.formatData(
        this.rawValues,
        this.config.fields.filter((field) => field.inDetail == true || field.inPdfExport == true)
      );
    },
    isHandled: function () {
      return this.rawValues.status && "complaint-traitee" == this.rawValues.status.status_slug;
    },
    canEdit: function () {
      return this.$store.getters["auth/isAuthorized"](config.authFeature + "_edit");
    },
    hasTargets: function () {
      return [
        "reference",
        "product",
        "renting",
        "business",
        "transfer",
        "business_return",
      ].some((name) => this.values[name]);
    },
    exportPdfData: function () {
      return {
        title: this.values[this.config.titleField],
        infos: this.config.fields
          .filter((field) => field.inPdfExport == true)
          .map((field) => [field.label + " :", this.values[field.name] || null]),
        lists:
          this.rawValues.stored_files && this.rawValues.stored_files.length
            ? [
                {
                  label: "Fichiers joints",
                  infos: [["Fichier"], ...this.rawValues.stored_files.map((f) => [f.file])],
                },
              ]
            : [],
      };
    },
  },
  watch: {
    // La zone de saisie suit les données : au chargement de la fiche comme après
    // chaque enregistrement, qui les recharge.
    "rawValues.comment": {
      immediate: true,
      handler(value) {
        this.comment = value || "";
      },
    },
  },
  mounted() {
    let self = this;

    this.config.fields.forEach((field) => {
      this.labels[field.name] = field.label;
    });

    this.isBusy = true;
    this.$store
      .dispatch(config.storeNamespace + "/getItem", {
        id: this.$route.params.id,
      })
      .then(() => {
        // Test si le contenu demandé est bien trouvé
        if (Object.entries(this.rawValues).length <= 1) {
          this.$toastr.error(
            "La fiche demandée n'a pas été trouvée. Assure-vous que l'URL saisie soit correcte."
          );
          setTimeout(function () {
            self.$router.go(-1);
          }, 5000);
          return;
        }
        this.isBusy = false;
      });
  },
  methods: {
    /**
     * Change le statut de la réclamation pour 'Traité'
     *
     * La remarque en cours de saisie part avec : celui qui note puis clôture dans la
     * foulée ne s'attend pas à la voir disparaître.
     */
    approuve() {
      this.save("complaint-traitee").then(() => {
        this.$toastr.success("La réclamation est marquée comme traitée");
      });
    },

    /** Enregistre les remarques sans toucher au statut. */
    saveComment() {
      this.isSaving = true;
      this.save(this.rawValues.status ? this.rawValues.status.status_slug : null)
        .then(() => {
          this.isSaving = false;
          this.$toastr.success("Les remarques ont été enregistrées");
        })
        .catch(() => {
          this.isSaving = false;
        });
    },

    /** L'API réclame le statut pour toute modification : il accompagne chaque envoi. */
    save(statusSlug) {
      return this.$store.dispatch(config.storeNamespace + "/saveItem", {
        ...this.rawValues,
        comment: this.comment,
        status_slug: statusSlug,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
h1 .badge {
  vertical-align: middle;
  margin-left: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/Complaint/ComplaintDetail.vue